import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import HRISConnector from './components/HRISConnector';
import IntegrationStoryPanel from './components/IntegrationStoryPanel';
import RealHRISIntegrationPanel from './components/RealHRISIntegrationPanel';
import CSVImportSection from './components/CSVImportSection';
import SyncScheduler from './components/SyncScheduler';
import DataQualityPanel from './components/DataQualityPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const DataIntegration = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('connectors');
  const [connectors, setConnectors] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [syncSchedules, setSyncSchedules] = useState([]);
  const [qualityMetrics, setQualityMetrics] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    // Initialize mock data
    setConnectors([
      {
        id: 'sap-sf',
        name: 'SAP SuccessFactors',
        description: 'Employee Core HR and Compensation data',
        icon: 'Building2',
        status: 'connected',
        lastSync: '2 hours ago',
        recordCount: 2847,
        dataQuality: 94,
        errorMessage: null
      },
      {
        id: 'workday',
        name: 'Workday HCM',
        description: 'Payroll and Benefits information',
        icon: 'Users',
        status: 'error',
        lastSync: '1 day ago',
        recordCount: 0,
        dataQuality: 0,
        errorMessage: 'Authentication failed. Please check credentials and retry connection.'
      },
      {
        id: 'bamboohr',
        name: 'BambooHR',
        description: 'Performance and Time tracking data',
        icon: 'Clock',
        status: 'disconnected',
        lastSync: 'Never',
        recordCount: 0,
        dataQuality: 0,
        errorMessage: null
      }
    ]);

    setImportHistory([
      {
        id: 'imp-001',
        filename: 'employee_data_q4_2024.csv',
        timestamp: '2024-10-08 10:30 AM',
        status: 'success',
        recordsProcessed: 1250,
        recordsImported: 1247,
        errorCount: 3,
        errorReport: true
      },
      {
        id: 'imp-002',
        filename: 'compensation_update_oct.csv',
        timestamp: '2024-10-07 02:15 AM',
        status: 'error',
        recordsProcessed: 890,
        recordsImported: 0,
        errorCount: 45,
        errorReport: true
      },
      {
        id: 'imp-003',
        filename: 'new_hires_september.csv',
        timestamp: '2024-10-06 09:45 AM',
        status: 'success',
        recordsProcessed: 67,
        recordsImported: 67,
        errorCount: 0,
        errorReport: false
      }
    ]);

    setSyncSchedules([
      {
        id: 'sched-001',
        name: 'Daily SAP Sync',
        connector: 'SAP SuccessFactors',
        frequency: 'daily',
        time: '02:00',
        enabled: true,
        nextRun: 'Tomorrow at 2:00 AM',
        lastRun: 'Today at 2:00 AM (Success)'
      },
      {
        id: 'sched-002',
        name: 'Weekly Payroll Update',
        connector: 'Workday HCM',
        frequency: 'weekly',
        time: '01:00',
        enabled: false,
        nextRun: 'Sunday at 1:00 AM',
        lastRun: 'Last Sunday (Failed)'
      }
    ]);

    setQualityMetrics({
      totalRecords: 2847,
      validRecords: 2679,
      validPercentage: 94,
      issuesFound: 168,
      completeness: 87,
      fieldCompleteness: [
        { name: 'employee_id', labelKey: 'dataIntegration.fields.employeeId',  completeness: 100 },
        { name: 'first_name',  labelKey: 'dataIntegration.fields.firstName',   completeness: 99 },
        { name: 'last_name',   labelKey: 'dataIntegration.fields.lastName',    completeness: 99 },
        { name: 'email',       labelKey: 'dataIntegration.fields.emailAddress', completeness: 95 },
        { name: 'department',  labelKey: 'dataIntegration.fields.department',  completeness: 92 },
        { name: 'salary',      labelKey: 'dataIntegration.fields.baseSalary',  completeness: 88 },
        { name: 'gender',      labelKey: 'dataIntegration.fields.gender',      completeness: 76 },
        { name: 'hire_date',   labelKey: 'dataIntegration.fields.hireDate',    completeness: 94 }
      ]
    });

    setValidationErrors([
      {
        id: 'err-001',
        type: 'missing',
        severity: 'high',
        titleKey:       'dataIntegration.errors.missingGender.title',
        descriptionKey: 'dataIntegration.errors.missingGender.description',
        field: 'gender',
        recordCount: 89,
        detectedHoursAgo: 2,
        // Affected sample (mock), used by the Details modal to give the
        // user a concrete sense of what's missing
        sampleRecords: [
          { id: 'EMP-1842', name: 'Hidden, privacy', department: 'Engineering', issue: 'gender field empty' },
          { id: 'EMP-2117', name: 'Hidden, privacy', department: 'Sales',       issue: 'gender field empty' },
          { id: 'EMP-2433', name: 'Hidden, privacy', department: 'Operations',  issue: 'gender field empty' },
        ],
        ruleKey: 'dataIntegration.errors.missingGender.rule',
        impactKey: 'dataIntegration.errors.missingGender.impact',
        fixHintKey: 'dataIntegration.errors.missingGender.fixHint',
      },
      {
        id: 'err-002',
        type: 'invalid',
        severity: 'medium',
        titleKey:       'dataIntegration.errors.invalidEmail.title',
        descriptionKey: 'dataIntegration.errors.invalidEmail.description',
        field: 'email',
        recordCount: 23,
        detectedHoursAgo: 3,
        sampleRecords: [
          { id: 'EMP-1042', name: 'Hidden, privacy', department: 'Marketing', issue: 'no @ symbol' },
          { id: 'EMP-1208', name: 'Hidden, privacy', department: 'Finance',   issue: 'invalid TLD' },
        ],
        ruleKey: 'dataIntegration.errors.invalidEmail.rule',
        impactKey: 'dataIntegration.errors.invalidEmail.impact',
        fixHintKey: 'dataIntegration.errors.invalidEmail.fixHint',
      },
      {
        id: 'err-003',
        type: 'duplicate',
        severity: 'high',
        titleKey:       'dataIntegration.errors.duplicateIds.title',
        descriptionKey: 'dataIntegration.errors.duplicateIds.description',
        field: 'employee_id',
        recordCount: 12,
        detectedHoursAgo: 1,
        sampleRecords: [
          { id: 'EMP-3091 (×2)', name: 'Hidden, privacy', department: 'Sales',     issue: 'duplicate id' },
          { id: 'EMP-3144 (×2)', name: 'Hidden, privacy', department: 'Engineering', issue: 'duplicate id' },
        ],
        ruleKey: 'dataIntegration.errors.duplicateIds.rule',
        impactKey: 'dataIntegration.errors.duplicateIds.impact',
        fixHintKey: 'dataIntegration.errors.duplicateIds.fixHint',
      },
      {
        id: 'err-004',
        type: 'mapping',
        severity: 'low',
        titleKey:       'dataIntegration.errors.deptMismatch.title',
        descriptionKey: 'dataIntegration.errors.deptMismatch.description',
        field: 'department',
        recordCount: 44,
        detectedHoursAgo: 4,
        sampleRecords: [
          { id: 'EMP-2050', name: 'Hidden, privacy', department: 'ENG-OLD',   issue: 'maps to inactive code' },
          { id: 'EMP-2083', name: 'Hidden, privacy', department: 'SALES-EU2', issue: 'unknown department' },
        ],
        ruleKey: 'dataIntegration.errors.deptMismatch.rule',
        impactKey: 'dataIntegration.errors.deptMismatch.impact',
        fixHintKey: 'dataIntegration.errors.deptMismatch.fixHint',
      }
    ]);
  }, []);

  const tabs = [
    { id: 'connectors', labelKey: 'dataIntegration.tabs.hrisConnectors', icon: 'Link' },
    { id: 'import', labelKey: 'dataIntegration.tabs.csvImport', icon: 'Upload' },
    { id: 'schedules', labelKey: 'dataIntegration.tabs.syncSchedules', icon: 'Calendar' },
    { id: 'quality', labelKey: 'dataIntegration.tabs.dataQuality', icon: 'Shield' }
  ];

  const handleConnectorConnect = async (connectorId) => {
    // Simulate connection process
    setConnectors(prev => prev?.map(conn => 
      conn?.id === connectorId 
        ? { ...conn, status: 'connected', lastSync: 'Just now', recordCount: 2847, dataQuality: 94 }
        : conn
    ));
  };

  const handleConnectorDisconnect = async (connectorId) => {
    setConnectors(prev => prev?.map(conn => 
      conn?.id === connectorId 
        ? { ...conn, status: 'disconnected', lastSync: 'Never', recordCount: 0, dataQuality: 0 }
        : conn
    ));
  };

  const handleConnectorSync = async (connectorId) => {
    // Simulate sync process
    setConnectors(prev => prev?.map(conn => 
      conn?.id === connectorId 
        ? { ...conn, status: 'syncing' }
        : conn
    ));

    setTimeout(() => {
      setConnectors(prev => prev?.map(conn => 
        conn?.id === connectorId 
          ? { ...conn, status: 'connected', lastSync: 'Just now' }
          : conn
      ));
    }, 3000);
  };

  const handleFileUpload = async (file, mappingConfig) => {
    // Simulate file upload and processing
    const newImport = {
      id: `imp-${Date.now()}`,
      filename: file?.name,
      timestamp: new Date()?.toLocaleString(),
      status: 'processing',
      recordsProcessed: 0,
      recordsImported: 0,
      errorCount: 0,
      errorReport: false
    };

    setImportHistory(prev => [newImport, ...prev]);

    // Simulate processing completion
    setTimeout(() => {
      setImportHistory(prev => prev?.map(imp => 
        imp?.id === newImport?.id 
          ? { ...imp, status: 'success', recordsProcessed: 1500, recordsImported: 1495, errorCount: 5 }
          : imp
      ));
    }, 5000);
  };

  const handleDownloadErrorReport = (importId) => {
    // Simulate error report download
    console.log('Downloading error report for import:', importId);
  };

  const handleCreateSchedule = async (scheduleData) => {
    const newSchedule = {
      id: `sched-${Date.now()}`,
      ...scheduleData,
      nextRun: 'Tomorrow at ' + scheduleData?.time,
      lastRun: 'Never'
    };
    setSyncSchedules(prev => [...prev, newSchedule]);
  };

  const handleUpdateSchedule = async (scheduleId, updates) => {
    setSyncSchedules(prev => prev?.map(sched => 
      sched?.id === scheduleId ? { ...sched, ...updates } : sched
    ));
  };

  const handleResolveError = async (errorId) => {
    setValidationErrors(prev => prev?.filter(err => err?.id !== errorId));
  };

  const handleRefreshValidation = async () => {
    // Simulate validation refresh
    console.log('Refreshing data validation...');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'connectors':
        return (
          <div className="space-y-6">
            <RealHRISIntegrationPanel />
            <IntegrationStoryPanel />
            {connectors?.map((connector) => (
              <HRISConnector
                key={connector?.id}
                connector={connector}
                onConnect={handleConnectorConnect}
                onDisconnect={handleConnectorDisconnect}
                onSync={handleConnectorSync}
              />
            ))}
          </div>
        );

      case 'import':
        return (
          <CSVImportSection
            onFileUpload={handleFileUpload}
            importHistory={importHistory}
            onDownloadErrorReport={handleDownloadErrorReport}
          />
        );

      case 'schedules':
        return (
          <SyncScheduler
            schedules={syncSchedules}
            onUpdateSchedule={handleUpdateSchedule}
            onCreateSchedule={handleCreateSchedule}
          />
        );

      case 'quality':
        return (
          <DataQualityPanel
            qualityMetrics={qualityMetrics}
            validationErrors={validationErrors}
            onResolveError={handleResolveError}
            onRefreshValidation={handleRefreshValidation}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('dataIntegration.pageTitle')}</h1>
              <p className="text-muted-foreground">
                {t('dataIntegration.pageSubtitle')}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
              >
                {t('dataIntegration.refreshAll')}
              </Button>
              <Button
                variant="default"
                iconName="Settings"
                iconPosition="left"
              >
                {t('dataIntegration.integrationSettings')}
              </Button>
            </div>
          </div>

          {/* Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Link" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('dataIntegration.stats.activeConnections')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {connectors?.filter(c => c?.status === 'connected')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Database" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('dataIntegration.stats.totalRecords')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {qualityMetrics?.totalRecords?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('dataIntegration.stats.dataIssues')}</p>
                  <p className="text-2xl font-bold text-foreground">{validationErrors?.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('dataIntegration.stats.scheduledSyncs')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {syncSchedules?.filter(s => s?.enabled)?.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-8">
            <nav className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{t(tab.labelKey)}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

export default DataIntegration;